import { getAccessToken } from "../forge/suite/auth";
import { loadSuiteSettings, SUITE_RELAY_URL } from "../forge/suite/settings";
import {
  forgeEndpointChatBase,
  HOUSE_CHAT_HF_NAME,
  isForgeEndpointChatUrl,
  normalizeOpenAiBaseUrl,
  suiteRelayChatBase,
} from "./endpoint";
import type { ChatSource, InferenceTarget } from "./types";

const KEY = "chatforge.inference.v1";

export function persistTarget(target: InferenceTarget): void {
  if (typeof localStorage === "undefined") return;
  const stored = {
    baseUrl: target.baseUrl,
    model: target.model,
    source: target.source,
    label: target.label,
    sourceId: target.sourceId,
    connectionId: target.connectionId,
  };
  localStorage.setItem(KEY, JSON.stringify(stored));
}

export function loadPersistedTarget(): InferenceTarget | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<InferenceTarget>;
    const model = (parsed.model || "").trim();
    let baseUrl = normalizeOpenAiBaseUrl(parsed.baseUrl || "");
    const source: ChatSource =
      parsed.source === "endpoint" || isForgeEndpointChatUrl(baseUrl)
        ? "endpoint"
        : "catalog";
    if (source === "endpoint") {
      const rebuilt = forgeEndpointChatBase({
        endpointUrl: baseUrl,
        sourceId: parsed.sourceId,
        connectionId: parsed.connectionId,
        forgeApiUrl: loadSuiteSettings().forgeApiUrl || SUITE_RELAY_URL,
      });
      if (rebuilt) baseUrl = rebuilt;
    }
    if (!model || !baseUrl) return null;
    return {
      baseUrl,
      apiKey: getAccessToken() || "",
      model,
      source,
      label: parsed.label || model,
      sourceId: parsed.sourceId,
      connectionId: parsed.connectionId,
    };
  } catch {
    return null;
  }
}

/** Rebuild endpoint URL + JWT right before send (persist never stores the key). */
export function refreshTargetForSend(target: InferenceTarget): InferenceTarget {
  const settings = loadSuiteSettings();
  const forge = settings.forgeApiUrl || SUITE_RELAY_URL;
  const apiKey = getAccessToken() || target.apiKey;
  if (target.source !== "endpoint") {
    return { ...target, apiKey };
  }
  const base =
    forgeEndpointChatBase({
      endpointUrl: target.baseUrl,
      sourceId: target.sourceId,
      connectionId: target.connectionId,
      forgeApiUrl: forge,
    }) || target.baseUrl;
  return { ...target, apiKey, baseUrl: base };
}

/** Public ChatForge talks to suite origin. AccessHub Pick / HF_CHAT_ENDPOINT choose the model. */
export function houseTarget(): InferenceTarget {
  const settings = loadSuiteSettings();
  const forge = settings.forgeApiUrl || SUITE_RELAY_URL;
  return {
    baseUrl: suiteRelayChatBase(forge),
    apiKey: getAccessToken() || "",
    model: HOUSE_CHAT_HF_NAME,
    source: "catalog",
    label: "House chat",
    sourceId: HOUSE_CHAT_HF_NAME,
  };
}

export function catalogTarget(opts: {
  model: string;
  label?: string;
  forgeApiUrl?: string;
}): InferenceTarget {
  const settings = loadSuiteSettings();
  const forge = opts.forgeApiUrl || settings.forgeApiUrl || SUITE_RELAY_URL;
  return {
    baseUrl: suiteRelayChatBase(forge),
    apiKey: getAccessToken() || "",
    model: opts.model,
    source: "catalog",
    label: opts.label || opts.model,
  };
}

export function endpointTarget(opts: {
  model: string;
  label?: string;
  endpointUrl?: string | null;
  sourceId?: string | null;
  connectionId?: string | null;
  forgeApiUrl?: string;
  apiKey?: string;
}): InferenceTarget {
  const settings = loadSuiteSettings();
  const forge = opts.forgeApiUrl || settings.forgeApiUrl || SUITE_RELAY_URL;
  const base =
    forgeEndpointChatBase({
      endpointUrl: opts.endpointUrl,
      sourceId: opts.sourceId,
      connectionId: opts.connectionId,
      forgeApiUrl: forge,
    }) || suiteRelayChatBase(forge);
  return {
    baseUrl: base,
    apiKey: opts.apiKey || getAccessToken() || "",
    model: opts.model,
    source: "endpoint",
    label: opts.label || opts.model,
    sourceId: opts.sourceId || undefined,
    connectionId: opts.connectionId || undefined,
  };
}
